import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Send, Star, X } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import { fetchAssignableUsers, sendPersonalTask } from '@/services/personalTasksService'
import { useAuth } from '@/contexts/AuthContext'
import api from '@/lib/axios'
import { useRewardsCurrency } from '@/hooks/useRewardsCurrency';

interface Props {
  open: boolean
  onClose: () => void
  presetAssignee?: string
  // Prefills — still editable, same "nothing is locked" philosophy as everything
  // else here. A caller (e.g. a Food Safety issue's "Send as task" verb) hands in
  // the issue's own title/context instead of making the sender retype it.
  initialTitle?: string
  initialNote?: string
  // Threaded straight through to sendPersonalTask so the task's origin shows up
  // wherever tasks report their source (e.g. 'Food Safety', 'Waste').
  source?: string
  // Fires after a successful send, before the dialog closes — lets the caller
  // chain a side effect keyed on who it went to (e.g. auto-park an issue noting
  // "task sent to <FirstName>").
  onSent?: (assignee: { _id: string; name: string }) => void
}

const MAX_TITLE = 200
const MAX_NOTE = 2000

interface Allocation {
  weeklyLimit: number
  remaining: number
  periodLabel?: string
}

// One person handing one other person one thing to do. Everything that isn't
// "who" and "what" is optional on purpose — the moment this needs a category,
// a priority and an owner it stops getting used from the floor.
export default function SendTaskDialog({ open, onClose, presetAssignee, initialTitle, initialNote, source, onSent }: Props) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const currency = useRewardsCurrency();

  const [assignedTo, setAssignedTo] = useState(presetAssignee ?? '')
  const [personQuery, setPersonQuery] = useState('')
  const [title, setTitle] = useState(initialTitle ?? '')
  const [note, setNote] = useState(initialNote ?? '')
  const [dueDate, setDueDate] = useState('')
  const [amount, setAmount] = useState('')
  const [allocation, setAllocation] = useState<Allocation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const inFlight = useRef(false)

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['assignable-users'],
    queryFn: fetchAssignableUsers,
    staleTime: 5 * 60 * 1000,
    enabled: open,
  })

  // Reopening for a different person must not inherit the last send's typing,
  // so the reset is keyed on the preset as well as on open.
  useEffect(() => {
    if (!open) return
    setAssignedTo(presetAssignee ?? '')
    setPersonQuery('')
    setTitle(initialTitle ?? '')
    setNote(initialNote ?? '')
    setDueDate('')
    setAmount('')
    setError(null)
    inFlight.current = false
  }, [open, presetAssignee, initialTitle, initialNote])

  // Same rule as chat's Recognize: a fresh look at the sender's CFAdollars
  // allowance each time it opens; any error (rewards off, no allowance) just
  // means no CFAdollars row.
  useEffect(() => {
    if (!open) return
    setAllocation(null)
    let cancelled = false
    api.get('/api/rewards/allocation', { suppressErrorToast: true } as never)
      .then(res => { if (!cancelled) setAllocation(res.data) })
      .catch(() => { if (!cancelled) setAllocation(null) })
    return () => { cancelled = true }
  }, [open])

  const send = useMutation({
    mutationFn: () => sendPersonalTask({
      assignedTo,
      title: title.trim(),
      note: note.trim() || undefined,
      dueDate: dueDate || undefined,
      source,
      ...(canAttachReward && parsedAmount > 0 && { rewardAmount: parsedAmount }),
    }),
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: ['myPrioritiesToday'] })
      toast({
        title: 'Task sent',
        ...(task.reward?.amount && { description: `${task.reward.amount} ${currency} set aside — paid when it's done.` }),
      })
      if (chosen) onSent?.(chosen)
      onClose()
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || 'Could not send that task'
      setError(message)
      toast({ title: message, variant: 'destructive' })
    },
    onSettled: () => { inFlight.current = false },
  })

  const chosen = users.find(u => u._id === assignedTo)

  // Typing filters; it never picks for you. Matching on position too so "drive"
  // or "director" narrows a long roster the way a name does.
  const matches = useMemo(() => {
    const q = personQuery.trim().toLowerCase()
    if (!q) return users
    return users.filter(u =>
      u.name.toLowerCase().includes(q) || (u.position || '').toLowerCase().includes(q)
    )
  }, [users, personQuery])

  // No CFAdollars for a task you send yourself, and none without allowance left.
  const isSelf = !!assignedTo && !!user?._id && assignedTo === user._id
  const unlimited = allocation?.weeklyLimit === -1
  const canAttachReward = !!allocation && !isSelf && (unlimited || allocation.remaining > 0)
  const parsedAmount = amount.trim() === '' ? 0 : Number(amount)
  const amountValid = !canAttachReward || (
    Number.isInteger(parsedAmount) && parsedAmount >= 0 && (unlimited || parsedAmount <= allocation!.remaining)
  )
  const periodWord = (allocation?.periodLabel || 'week').toLowerCase().replace(/ly$/, '')

  const canSend = !!assignedTo && title.trim().length > 0 && amountValid

  // The guard is here and not only on `disabled` because the form also submits
  // on Enter. The ref rather than `isPending` because two taps inside one
  // render can both read the old pending value and send the task twice.
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSend || inFlight.current || send.isPending) return
    inFlight.current = true
    setError(null)
    send.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={next => !next && onClose()}>
      <DialogContent className="pointer-events-auto sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-base">Send a task</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Who is this for?
            </Label>
            {chosen ? (
              // Once someone is picked the list gets out of the way — the common
              // case is one person, and a roster still on screen just reads as
              // unfinished.
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="truncate text-sm">
                  <span className="font-medium text-slate-900">{chosen.name}</span>
                  {chosen.position && <span className="text-slate-400"> — {chosen.position}</span>}
                </span>
                <button
                  type="button"
                  onClick={() => { setAssignedTo(''); setPersonQuery('') }}
                  className="ml-2 shrink-0 rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                  aria-label="Choose someone else"
                  title="Choose someone else"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <>
                <Input
                  id="send-task-who"
                  value={personQuery}
                  onChange={e => setPersonQuery(e.target.value)}
                  placeholder={usersLoading ? 'Loading people…' : 'Start typing a name'}
                  autoComplete="off"
                  aria-label="Who is this for?"
                />
                {/* Capped height rather than a full roster: a store can have 120
                    people, which is the scroll this replaced. */}
                <div
                  role="listbox"
                  aria-label="Matching people"
                  className="max-h-44 overflow-y-auto rounded-md border border-slate-200"
                >
                  {matches.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-slate-400">
                      {usersLoading ? 'Loading people…' : `Nobody matches “${personQuery.trim()}”`}
                    </p>
                  ) : (
                    matches.map(u => (
                      <button
                        key={u._id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        onClick={() => { setAssignedTo(u._id); setPersonQuery('') }}
                        className="flex w-full items-baseline gap-1.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        <span className="truncate text-slate-900">{u.name}</span>
                        {u.position && (
                          <span className="truncate text-xs text-slate-400">{u.position}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="send-task-title"
              className="text-xs font-semibold uppercase tracking-wide text-slate-400"
            >
              What needs doing?
            </Label>
            <Input
              id="send-task-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={MAX_TITLE}
              placeholder="Change the bulb over table 12"
              autoComplete="off"
            />
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="send-task-note"
              className="text-xs font-semibold uppercase tracking-wide text-slate-400"
            >
              Anything else?
            </Label>
            <Textarea
              id="send-task-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={MAX_NOTE}
              rows={3}
              placeholder="Optional"
            />
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="send-task-due"
              className="text-xs font-semibold uppercase tracking-wide text-slate-400"
            >
              Due date
            </Label>
            <Input
              id="send-task-due"
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
            />
          </div>

          {canAttachReward && (
            <div className="space-y-1.5">
              <Label
                htmlFor="send-task-reward"
                className="text-xs font-semibold uppercase tracking-wide text-slate-400"
              >
                {currency} <span className="normal-case tracking-normal font-medium">(optional)</span>
              </Label>
              <div className="flex items-center gap-3">
                <div className="relative w-28">
                  <Star className="absolute left-2.5 top-2.5 h-4 w-4 fill-amber-500 text-amber-500" />
                  <Input
                    id="send-task-reward"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="0"
                    className={`pl-8 ${amountValid ? '' : 'border-red-400'}`}
                  />
                </div>
                <p className="text-xs text-gray-600">
                  {unlimited
                    ? `You have unlimited ${currency} to award`
                    : <>You have <span className="font-semibold text-amber-700">{allocation!.remaining} left</span> this {periodWord}</>}
                </p>
              </div>
              <p className="text-[11px] leading-snug text-gray-400">
                Set aside now and paid to {chosen ? chosen.name.split(' ')[0] : 'them'} the moment they mark it done. Cancel the task and you get them back.
              </p>
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-[#E51636]">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose} disabled={send.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSend || send.isPending}
              className="bg-[#E51636] text-white hover:bg-[#DD0031]"
            >
              {send.isPending
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Send className="mr-2 h-4 w-4" />}
              Send task
              {canAttachReward && parsedAmount > 0 && amountValid && (
                <span className="ml-1 inline-flex items-center gap-0.5">· <Star className="h-3.5 w-3.5 fill-current" />{parsedAmount}</span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
